import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CircleNotch, WarningCircle } from "@phosphor-icons/react";
import { Brand } from "@/components/Brand";
import { OutputTerminal } from "@/components/OutputTerminal";
import { MetricsPanel } from "@/components/MetricsPanel";
import { getPrompt, saveLibraryApi, getConfig } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SharePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const [tools, setTools] = useState([]);
  useEffect(() => { getConfig().then((c) => setTools(c.target_tools || [])).catch(() => {}); }, []);

  useEffect(() => {
    getPrompt(id).then((r) => { setResult(r); setState("ok"); }).catch(() => setState("error"));
  }, [id]);

  const doSave = async () => {
    if (!user) return navigate("/login");
    try { await saveLibraryApi(result); setSaved(true); toast.success("Saved to your library"); }
    catch { toast.error("Could not save"); }
  };

  return (
    <div className="stp-bg">
      <header className="stp-glass sticky top-0 z-30 border-x-0 border-t-0 border-b border-black/5">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-5 sm:px-8">
          <Brand testId="home-link" />
          <Link to="/" data-testid="create-own-button" className="rounded-full bg-ink px-4 py-1.5 text-sm font-medium text-white hover:bg-black">Create your own</Link>
        </div>
      </header>
      <main className="mx-auto w-full max-w-4xl px-5 pb-28 pt-14 sm:px-8">
        {state === "loading" && <div className="flex h-64 flex-col items-center justify-center text-mist" data-testid="share-loading"><CircleNotch size={32} className="animate-spin text-brand" /><p className="mt-3 text-sm">Loading shared prompt…</p></div>}
        {state === "error" && (
          <div className="flex h-64 flex-col items-center justify-center text-center" data-testid="share-error">
            <WarningCircle size={40} weight="duotone" className="text-[#FF3B30]" />
            <p className="mt-3 text-mist">This prompt could not be found.</p>
            <Link to="/" className="mt-5 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white">Go to SpeakToPrompt</Link>
          </div>
        )}
        {state === "ok" && result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-6">
            <div>
              <p className="text-sm font-semibold text-brand">Shared prompt</p>
              <h1 className="mt-2 font-display text-4xl font-semibold text-ink sm:text-5xl">Ready to paste into {tools.find((t) => t.id === result.target_tool)?.label || result.target_tool}.</h1>
              {result.input_text && <p className="mt-3 text-mist" data-testid="share-original-idea">Idea: “{result.input_text.slice(0, 200)}”</p>}
            </div>
            <OutputTerminal title={`Prompt · ${result.model}`} text={result.prompt} onSave={doSave} saved={saved} target={result.target_tool} />
            <MetricsPanel result={result} />
          </motion.div>
        )}
      </main>
    </div>
  );
}
